import { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Stack, Typography, LinearProgress, Alert, Chip, Box } from '@mui/material';
import Page from 'src/components/Page';
import { commandCenterApi } from 'src/utils/commandCenterApi';
import CommandCenterFilters from './CommandCenterFilters';
import DrillDrawer from './DrillDrawer';
import KpiBand from './KpiBand';
import FunnelSection from './FunnelSection';
import SegmentationSection from './SegmentationSection';
import FinancialBreakdown from './FinancialBreakdown';
import VelocityRisk from './VelocityRisk';
import CashSection from './CashSection';
import TrendsSection from './TrendsSection';

const STORAGE_KEY = 'command_center_filters';

const DEFAULT_FILTERS = {
  startDate: null,
  endDate: null,
  currencyId: null,
  countries: [],
  practices: [],
  offerings: [],
  segments: [],
  businessTypes: [],
  teams: [],
  owners: [],
  partners: [],
  trendMonths: 12,
};

function loadFilters() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_FILTERS, ...JSON.parse(raw) };
  } catch (e) {
    // ignore malformed cache
  }
  return DEFAULT_FILTERS;
}

// Maps the UI filter state → the backend query params (contract Section 5).
function buildQueryParams(f) {
  const ids = (arr) => (arr || []).map((x) => x.id).join(',');
  const p = {};
  if (f.currencyId) p.currency_id = f.currencyId;
  if (f.startDate) p.start_date = f.startDate;
  if (f.endDate) p.end_date = f.endDate;
  if (f.countries?.length) p.country_id = ids(f.countries);
  if (f.practices?.length) p.practice_id = ids(f.practices);
  if (f.offerings?.length) p.offering_id = ids(f.offerings);
  if (f.segments?.length) p.segment_id = ids(f.segments);
  if (f.businessTypes?.length) p.business_type_id = ids(f.businessTypes);
  if (f.teams?.length) p.team_id = ids(f.teams);
  if (f.owners?.length) p.owner_id = ids(f.owners);
  if (f.partners?.length) p.partner_id = ids(f.partners);
  if (f.trendMonths) p.trend_months = f.trendMonths;
  return p;
}

// SUPER_ADMIN-only executive Command Center. Route-gated in routes.js; the backend
// also returns 403 for non-super-admins (SC-004). Every widget reads from ONE `summary`
// fetched by ONE effect keyed on every filter incl. currency (SC-003).
export default function CommandCenter() {
  const [filters, setFilters] = useState(loadFilters);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drill, setDrill] = useState(null); // { params, title } | null

  const summaryParams = useMemo(() => buildQueryParams(filters), [filters]);
  const paramsKey = JSON.stringify(summaryParams);

  // Persist filters (mirror Dashboard's `dashboard_filters`).
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    } catch (e) {
      // quota / serialization errors are non-fatal
    }
  }, [filters]);

  // THE single fetch — keyed on the full filter set (incl. currency) via paramsKey (SC-003).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    commandCenterApi
      .getSummary(summaryParams)
      .then((res) => {
        if (cancelled) return;
        setSummary(res?.data?.data || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setSummary(null);
        setError(err?.response?.data?.message || 'Failed to load the Command Center.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  // Effective currency: user's pick, else the backend's default (most-common).
  const selectedCurrencyId = filters.currencyId ?? summary?.selected_currency?.id ?? null;
  const currencyLabel = summary?.selected_currency?.label;

  // Drill base = current filters WITHOUT currency_id; money drills re-add currency_id
  // via their paramsObj so counts reconcile (no currency) and money reconciles (currency).
  const drillBase = useMemo(() => {
    const { currency_id, ...rest } = summaryParams;
    return rest;
  }, [summaryParams]);

  const openDrill = useCallback(
    (paramsObj, title) => {
      setDrill({ params: { ...drillBase, ...paramsObj }, title });
    },
    [drillBase],
  );

  return (
    <Page title="Command Center | CRM">
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h4" fontWeight={700}>Command Center</Typography>
            <Typography variant="body2" color="text.secondary">Executive analytics · read-only</Typography>
          </Box>
          {summary?.selected_currency && (
            <Chip color="primary" variant="outlined" label={`Currency: ${summary.selected_currency.label}`} />
          )}
        </Stack>

        <CommandCenterFilters
          filters={filters}
          onChange={setFilters}
          availableCurrencies={summary?.available_currencies || []}
          selectedCurrencyId={selectedCurrencyId}
        />

        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {summary && (
          <>
            <KpiBand
              kpi={summary.kpi}
              currencyId={selectedCurrencyId}
              currencyLabel={currencyLabel}
              openDrill={openDrill}
            />
            <FunnelSection
              funnel={summary.funnel}
              currencyId={selectedCurrencyId}
              currencyLabel={currencyLabel}
              openDrill={openDrill}
            />
            <SegmentationSection
              segmentation={summary.segmentation}
              currencyId={selectedCurrencyId}
              currencyLabel={currencyLabel}
              openDrill={openDrill}
            />
            <FinancialBreakdown
              financial={summary.financial_breakdown}
              currencyLabel={currencyLabel}
            />
            <VelocityRisk
              velocity={summary.velocity_risk}
              currencyId={selectedCurrencyId}
              currencyLabel={currencyLabel}
              openDrill={openDrill}
            />
            <CashSection
              cash={summary.cash}
              currencyLabel={currencyLabel}
            />
            <TrendsSection
              trends={summary.trends}
              currencyLabel={currencyLabel}
            />
          </>
        )}

        <DrillDrawer
          open={!!drill}
          onClose={() => setDrill(null)}
          title={drill?.title}
          params={drill?.params}
        />
      </Container>
    </Page>
  );
}
